import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { gql } from 'graphql-request';
import { PracticeAnalyticsAggregations, PracticeAnalyticsApi, PracticeAnalyticsTypes } from '@frontend/api-analytics';
import { useAnalyticsOrgLocations, TabbedTable } from '@frontend/analytics-shared';
import { processExportableData } from '@frontend/file-download-helper';
import { useTranslation } from '@frontend/i18n';
import { useScopedQuery } from '@frontend/scope';
import { formatters, useIsSmallMode } from '@frontend/shared';
import { emptyStateGraphics, TableColumnConfig, useAlert } from '@frontend/design-system';
import { useShowBulkMessageButton } from '../../../hooks/use-show-bulk-message-button';
import { queryKeys } from '../../../query-keys';
import { trackingIds } from '../../../tracking-ids';
import { drillDownTableSize } from '../../../views/common-styles';
import { InlineStats } from '../../inline-stats';
import { BulkMessageButton } from '../bulk-message-button';
import { collectPatientIds, deriveExportValue } from '../helpers/patients';
import { getPracticeAnalyticsQueryVariables } from '../helpers/query-variables';
import { useSubViewTableColConfig } from '../helpers/subview-table-col-config';
import { usePatientsLastContacted } from '../hooks/use-patients-last-contacted';
import { usePracticeAnalyticsShallowStore } from '../hooks/use-practice-analytics-store';
import { HygieneFollowUpMetric } from '../hygiene-follow-up-metric';
import { SmallModePatientsList } from '../small-mode-patients-list';

type Tab = 'all' | 'sameDay' | 'unscheduled';

const query = gql`
  query ($start: Int!, $end: Int!, $step: TimeStep!) {
    location {
      hygieneReappointment {
        details(start: $start, end: $end, step: $step) {
          patients {
            id
            productionAmount
            hygieneAppointmentDate
            nextHygieneAppointmentDate
            scheduledSameDay
            nextAppointmentDate
            FirstName: firstName
            LastName: lastName
            PMID: pmid
            MobilePhone: mobilePhone
            HomePhone: homePhone
            WorkPhone: workPhone
            Email: email
            PreferredName: preferredName
            Birthdate: birthdate
            Gender: gender
          }
        }
        totals(start: $start, end: $end, step: $step) {
          patients
          rescheduledSameDay
          unscheduled
        }
      }
    }
  }
`;

type CategorizedFollowUps = {
  all: PracticeAnalyticsTypes.PatientInfo[];
  sameDay: PracticeAnalyticsTypes.PatientInfo[];
  unscheduled: PracticeAnalyticsTypes.PatientInfo[];
};

export const HygieneFollowUpSubView: FC<React.PropsWithChildren<PracticeAnalyticsTypes.MetricSubViewProps>> = ({
  exportEnabled,
  onPatientsLoad,
}) => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { isSmallMode } = useIsSmallMode();
  const { demoData, filters, isDemoAccount, showDemoChipAndBanner } = usePracticeAnalyticsShallowStore(
    'demoData',
    'filters',
    'isDemoAccount',
    'showDemoChipAndBanner'
  );
  const { locationNames } = useAnalyticsOrgLocations({ isDemoAccount, module: 'PA' });
  const isBulkMessagingEnabled = useShowBulkMessageButton();

  const [categorizedData, setCategorizedData] = useState<CategorizedFollowUps>({
    all: [],
    sameDay: [],
    unscheduled: [],
  });

  const { data, isLoading } = useScopedQuery({
    queryKey: queryKeys.practiceAnalyticsCharts(`hygieneFollowUpSubView-${isDemoAccount}-${JSON.stringify(filters)}`),
    queryFn: () =>
      isDemoAccount || !filters.locations?.length
        ? null
        : PracticeAnalyticsApi.getPracticeAnalyticsRecords<PracticeAnalyticsTypes.HygieneFollowUpResponse>({
            locationIds: filters.locations,
            queries: [query],
            variables: getPracticeAnalyticsQueryVariables(filters),
          }),
    onError: () => {
      alert.error(t("Couldn't load the dashboard data. Please try again."));
    },
    select: (data) => (isDemoAccount ? demoData?.hygieneFollowUp : data),
    retry: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const processedData = useMemo(() => {
    return {
      aggregatedData: PracticeAnalyticsAggregations.hygieneFollowUpDetails(data),
      patientIds: Object.values(data?.data || {}).reduce((acc, locationDetails) => {
        return collectPatientIds(locationDetails?.location?.hygieneReappointment?.details?.patients, acc);
      }, [] as string[]),
    };
  }, [data]);

  const { isLoading: isLoadingLastContacted, lastContactedDates } = usePatientsLastContacted({
    patientIds: processedData.patientIds,
  });

  useEffect(() => {
    const patients = processedData.aggregatedData.hygieneReappointment.details?.patients || [];

    setCategorizedData(
      patients.reduce(
        (final, patient) => {
          let value = { ...final, all: [...final.all, patient] };
          if (patient.scheduledSameDay) {
            value = { ...value, sameDay: [...value.sameDay, patient] };
          }
          if (!patient.nextHygieneAppointmentDate) {
            value = { ...value, unscheduled: [...value.unscheduled, patient] };
          }
          return value;
        },
        { all: [], sameDay: [], unscheduled: [] } as CategorizedFollowUps
      )
    );
    onPatientsLoad?.(patients);
  }, [onPatientsLoad, processedData]);

  const processExportableDataHandler = useCallback(
    (data: PracticeAnalyticsTypes.PatientInfo[]) =>
      // Translation is not needed as this is for export purpose
      processExportableData({
        columns: [
          'Patient Name',
          ...((filters.locations?.length || 0) > 1 ? ['Location Name'] : []),
          'Phone Number',
          'Email',
          'Completed Hygiene',
          'Last Contacted',
          '$ Value',
        ],
        data,
        deriveExportValue: (params) =>
          deriveExportValue({
            ...params,
            lastContactedDates,
            locationNames,
          }),
        sortColumn: 'productionAmount',
      }),
    [filters.locations, lastContactedDates, locationNames]
  );

  const tabs = useMemo(
    () => ({
      all: {
        data: categorizedData.all,
        label: t('All'),
        trackingId: trackingIds.practiceAnalytics.hygieneFollowUpAllTab,
      },
      sameDay: {
        data: categorizedData.sameDay,
        label: t('Same Day'),
        trackingId: trackingIds.practiceAnalytics.hygieneFollowUpSameDayTab,
      },
      unscheduled: {
        data: categorizedData.unscheduled,
        label: t('Unscheduled'),
        trackingId: trackingIds.practiceAnalytics.hygieneFollowUpUnscheduledTab,
      },
    }),
    [categorizedData, t]
  );

  const smallModeTableProps: PracticeAnalyticsTypes.SmallModePatientsListProps<Tab> = {
    exportFileName: 'Hygiene Follow Up Patients', // Translation not needed
    initialTab: 'all',
    isBulkMessagingEnabled,
    isDemoAccount,
    isExportEnabled: exportEnabled,
    isLoadingLastContacted,
    isLoadingPatients: isLoading,
    processExportableData: processExportableDataHandler,
    tabs,
  };

  const largeModeTableProps: PracticeAnalyticsTypes.LargeModeTableProps<Tab> = {
    ...smallModeTableProps,
    lastContactedDates,
    locationNames,
    multiLocations: (filters.locations?.length || 0) > 1,
  };

  return (
    <>
      <InlineStats
        data={[
          {
            key: 'hygieneVisits',
            name: t('Hygiene Visits'),
            value: formatters.value(processedData.aggregatedData.hygieneReappointment.totals.patients),
          },
          isSmallMode
            ? null
            : {
                key: 'reappointmentSameDay',
                name: t('Reappointment Same Day'),
                value: formatters.value(
                  processedData.aggregatedData.hygieneReappointment.totals.rescheduledSameDay || 0
                ),
              },
          {
            key: 'notYetRescheduled',
            name: t('Not Yet Rescheduled'),
            value: formatters.value(
              Math.round(processedData.aggregatedData.hygieneReappointment.totals.unscheduled || 0)
            ),
          },
        ]}
        isDemoAccount={showDemoChipAndBanner}
        isLoading={isLoading}
      />
      <HygieneFollowUpMetric clickNoop isDrillDownPage />
      {isSmallMode ? (
        <SmallModePatientsList<Tab> {...smallModeTableProps} trackingIdBase='hygiene-followup-patients-small-mode' />
      ) : (
        <LargeModeTable {...largeModeTableProps} />
      )}
    </>
  );
};

const LargeModeTable = ({
  isBulkMessagingEnabled,
  isExportEnabled,
  isLoadingLastContacted,
  isLoadingPatients,
  lastContactedDates,
  tabs,
  ...rest
}: PracticeAnalyticsTypes.LargeModeTableProps<Tab>) => {
  const { t } = useTranslation('analytics');

  const middleColumns: TableColumnConfig<PracticeAnalyticsTypes.PatientInfo>[] = [
    {
      Header: t('Completed Hygiene'),
      headerLabel: t('Completed Hygiene'),
      accessor: ({ hygieneAppointmentDate }) => formatters.date(hygieneAppointmentDate),
      id: 'hygieneAppointmentDate',
      sortType: 'date',
      sortInverted: true,
      width: 200,
    },
  ];

  const tabsCommonConfig = {
    colConfig: useSubViewTableColConfig({
      actionsTrackingIdBase: 'hygiene-follow-up-patient',
      middleColumns,
      patientsLastContacted: {
        isLoading: isLoadingLastContacted,
        lastContactedDates,
      },
    }),
    emptyStateConfig: 'users' as keyof typeof emptyStateGraphics,
    tableInstanceId: 'hygieneFollowUpPracticeSubView',
  };

  return (
    <TabbedTable<PracticeAnalyticsTypes.PatientInfo>
      {...rest}
      actions={BulkMessageButton}
      autorizedToExport={PracticeAnalyticsApi.auditPracticeAnalyticsExport}
      isExportReady={!isLoadingPatients && !isLoadingLastContacted}
      isLoading={isLoadingPatients}
      isSelectable={isBulkMessagingEnabled}
      showExportIcon={isExportEnabled}
      tabs={{
        all: {
          ...tabsCommonConfig,
          ...tabs.all,
          tableInstanceId: 'all-patients',
        },
        sameDay: {
          ...tabsCommonConfig,
          ...tabs.sameDay,
          tableInstanceId: 'same-day-patients',
        },
        unscheduled: {
          ...tabsCommonConfig,
          ...tabs.unscheduled,
          tableInstanceId: 'unscheduled-patients',
        },
      }}
      trackingIdBase='hygiene-follow-up-patients'
      wrapperStyle={drillDownTableSize}
    />
  );
};
